import React, { useEffect, useState } from "react";
import { useClients } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlatformBadge } from "@/components/Badges";
import { BarChart3, Eye, Heart, MessageCircle, Share2, Bookmark, MousePointerClick, TrendingUp, TrendingDown, FileText, RefreshCw, CheckCircle2, Clock, Download } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

const fmt = (n) => Intl.NumberFormat("de-DE").format(n || 0);
const apiErr = (e) => e?.response?.data?.detail || e?.message || "Fehler";

export default function Analyse() {
  const { user } = useAuth();
  const readOnly = Boolean(user?.review_access?.read_only);
  const { current } = useClients();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [analyticsAccess, setAnalyticsAccess] = useState(null);
  const [tiktokBusy, setTiktokBusy] = useState(false);

  const loadReports = () => { if (current) api.get(`/reports?client_id=${current.id}`).then(({ data }) => setReports(data)).catch(() => {}); };
  const loadOverview = () => {
    if (!current) return;
    setData(null);
    api.get(`/analytics/overview?client_id=${current.id}`).then(({ data }) => setData(data)).catch(() => setData({ empty: true }));
  };
  useEffect(() => {
    if (!current) return;
    loadOverview();
    loadReports();
    setAnalyticsAccess(null);
    api.get(`/analytics/access?client_id=${current.id}`).then(({data}) => setAnalyticsAccess(data.tiktok)).catch(() => {});
  }, [current]);

  const connectTikTokAnalytics = async () => {
    setTiktokBusy(true);
    try {
      const { data } = await api.post('/social/tiktok/connect', {client_id:current.id,purpose:'analytics'});
      window.location.href = data.auth_url;
    } catch (e) { toast.error(apiErr(e)); setTiktokBusy(false); }
  };
  const refreshTikTokAnalytics = async () => {
    setTiktokBusy(true);
    try {
      const { data } = await api.post('/analytics/tiktok/refresh',{client_id:current.id});
      toast.success(`${data.refreshed || 0} TikTok-Beiträge aktualisiert`);
      if (data.errors?.length) toast.warning(`${data.errors.length} Beiträge noch ohne abrufbare Kennzahlen. Öffentliche Post-ID und Sichtbarkeit prüfen.`);
      loadOverview();loadReports();
    } catch (e) { toast.error(apiErr(e)); }
    finally {setTiktokBusy(false);}
  };
  useEffect(() => {
    if (!current || new URLSearchParams(window.location.search).get('social_connected') !== 'tiktok_analytics') return;
    api.post('/analytics/tiktok/refresh',{client_id:current.id}).then(() => {loadOverview();loadReports();}).catch(e => toast.error(apiErr(e)));
  }, [current]);
  useEffect(() => {
    const error = new URLSearchParams(window.location.search).get('social_error');
    if (error === 'tiktok_analytics_account_mismatch') toast.error('Bitte dasselbe TikTok-Konto wie für die Posting-Verbindung verwenden. Die gespeicherte Verbindung wurde nicht überschrieben.');
    if (error === 'tiktok_analytics_scope_missing') toast.error('TikTok-Leserechte fehlen oder bestehende Rechte wurden nicht bestätigt. Die gespeicherte Verbindung wurde nicht überschrieben.');
  }, []);

  const syncPublished = async () => {
    setSyncing(true);
    try {
      const { data: result } = await api.post("/social/sync-published", { client_id: current.id });
      const created = (result.results || []).reduce((sum, row) => sum + (row.created || 0), 0);
      const updated = (result.results || []).reduce((sum, row) => sum + (row.updated || 0), 0);
      const failed = (result.results || []).filter((row) => row.status === "error");
      toast.success(`${created} neue und ${updated} vorhandene Plattform-Posts synchronisiert.`);
      if (failed.length) {
        toast.warning(failed.map((row) => `${row.platform}: ${row.message || "Synchronisierung fehlgeschlagen"}`).join(" · "));
      }
      loadOverview();
      loadReports();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSyncing(false);
    }
  };

  const makeReport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reports/weekly", { client_id: current.id });
      toast.success(`Wochenreport erstellt: ${data.posts_published} Post(s)`);
      loadReports();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const download = async (fmt) => {
    if (!latest) return;
    try {
      const res = await api.get(`/reports/${latest.id}/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wochenreport_${current.name}_${(latest.to || "").slice(0, 10)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!current) return <div className="text-slate-400">Kein Mandant ausgewählt.</div>;

  const latest = reports[0];
  const rt = latest?.totals || {};
  const engagement = (rt.likes || 0) + (rt.comments || 0) + (rt.shares || 0) + (rt.saves || 0);

  return (
    <div data-testid="analyse-page">
      <PageHeader overline={current.name} title="Analyse & Reporting" subtitle="Wochenreport, Reichweite und Engagement – pro Mandant." />

      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          onClick={syncPublished}
          disabled={syncing || readOnly}
          data-testid="sync-published-btn"
          className="rounded-full"
        >
          <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
          Plattform-Posts synchronisieren
        </Button>
      </div>

      {/* ---- Wochenreport (mandantenbezogen) ---- */}
      <Card className="p-5 rounded-md border-slate-200 shadow-none mb-6" data-testid="weekly-report-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-slate-500" />
            <span className="overline">Aktueller Wochenreport</span>
            {latest && (latest.data_available
              ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle2 size={11} /> Live-Daten</span>
              : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><Clock size={11} /> Kennzahlen nach Go-Live</span>)}
          </div>
          <div className="flex items-center gap-2">
            {latest && <Button size="sm" variant="outline" onClick={() => download("csv")} data-testid="export-csv-btn" className="rounded-full h-8"><Download size={13} className="mr-1.5" /> CSV</Button>}
            {latest && <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid="export-pdf-btn" className="rounded-full h-8"><Download size={13} className="mr-1.5" /> PDF</Button>}
            <Button size="sm" onClick={makeReport} disabled={busy || readOnly} data-testid="generate-report-btn" className="rounded-full h-8 bg-[#0A0A0A]">
              {busy ? <RefreshCw size={13} className="animate-spin" /> : <><RefreshCw size={13} className="mr-1.5" /> Report erstellen</>}
            </Button>
          </div>
        </div>

        {!latest ? (
          <div className="text-sm text-slate-400 py-6 text-center" data-testid="no-report">Noch kein Wochenreport. Erstelle den ersten für {current.name}.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {[["Posts veröffentlicht", latest.posts_published], ["Views", rt.views], ["Engagement", engagement], ["Reichweite", rt.reach]].map(([l, v]) => (
                <div key={l} className="border border-slate-100 rounded-sm px-3 py-2.5">
                  <div className="overline !text-[10px]">{l}</div>
                  <div className="font-display text-2xl font-extrabold mt-0.5">{fmt(v)}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-slate-400 font-mono mr-1">
                {latest.from?.slice(0, 10)} → {latest.to?.slice(0, 10)} · erstellt {latest.generated_at ? new Date(latest.generated_at).toLocaleString("de-DE") : "—"}
              </span>
              <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                Historischer Snapshot
              </span>
              {Object.entries(latest.posts_by_platform || {}).map(([p, n]) => (
                <span key={p} className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-100 rounded-full px-2 py-1">
                  <PlatformBadge platform={p} /> <span className="font-mono">{n}</span>
                </span>
              ))}
            </div>
            {reports.length > 1 && (
              <div className="mt-4 pt-3 border-t border-slate-100">
                <div className="overline !text-[10px] mb-2">Frühere Reports</div>
                <div className="divide-y divide-slate-100" data-testid="report-history">
                  {reports.slice(1, 6).map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-1.5 text-sm">
                      <span className="text-slate-500 font-mono text-xs">{r.generated_at ? new Date(r.generated_at).toLocaleString("de-DE") : "—"}</span>
                      <span className="text-slate-600">{r.posts_published} Posts · {fmt(r.totals?.views)} Views</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {analyticsAccess && analyticsAccess.state !== 'restricted' && <Card data-testid="tiktok-analytics-status" className="p-4 rounded-md border-slate-200 shadow-none mb-6">
        <div className="font-semibold text-sm mb-2">TikTok-Statistiken</div>
        <p className="text-sm text-slate-600 mb-3">{analyticsAccess.available
          ? "Leserechte vorhanden: Views, Likes, Kommentare und Shares öffentlicher TikTok-Videos können abgerufen werden. Nicht verfügbare Kennzahlen sind keine Null-Aufrufe."
          : analyticsAccess.app_read_enabled
            ? "Bitte das vorhandene TikTok-Konto mit der zusätzlichen Berechtigung video.list bestätigen. Die Veröffentlichung und die Statistik-Leserechte sind getrennt."
            : "Die Statistik-Leserechte sind noch nicht freigeschaltet. Display API und video.list müssen in der Live-App genehmigt und serverseitig aktiviert werden; danach das Konto mit den zusätzlichen Leserechten verbinden. Bis dahin sind keine echten TikTok-Kennzahlen abrufbar."}</p>
        {!readOnly && !user?.review_access && analyticsAccess.available && <Button data-testid="tiktok-analytics-refresh" variant="outline" disabled={tiktokBusy} onClick={refreshTikTokAnalytics}>TikTok-Kennzahlen aktualisieren</Button>}
        {!readOnly && !user?.review_access && !analyticsAccess.available && <Button data-testid="tiktok-analytics-connect" variant="outline" disabled={tiktokBusy || !analyticsAccess.app_read_enabled || !analyticsAccess.connected} onClick={connectTikTokAnalytics}>TikTok-Statistiken verbinden</Button>}
      </Card>}

      {/* ---- Detail-Overview (falls Kennzahlen vorhanden) ---- */}
      {data && !data.empty && <Overview data={data} />}
      {data && data.empty && (
        <EmptyState icon={BarChart3} title="Noch keine Detail-Kennzahlen" hint="Sobald Posts veröffentlicht sind (nach Go-Live), erscheinen hier Reichweite, Engagement und Top-/Flop-Posts." />
      )}
    </div>
  );
}

const Overview = ({ data }) => {
  const t = data.totals;
  const platformViews = (data.by_platform || []).map((row) => ({
    ...row,
    chart_views: row.views_available === false ? null : row.views,
  }));
  const unavailableViews = platformViews.filter((row) => row.views_available === false);
  const partialViews = platformViews.filter((row) => row.views_partially_available === true);
  const kpis = [
    ["Reichweite", t.reach, Eye], ["Views", t.views, Eye], ["Likes", t.likes, Heart],
    ["Kommentare", t.comments, MessageCircle], ["Shares", t.shares, Share2], ["Saves", t.saves, Bookmark],
    ["Klicks", t.clicks, MousePointerClick], ["Neue Follower", t.followers, TrendingUp],
  ];
  return (
    <div>
      <div className="mb-3">
        <div className="overline !text-slate-600">Gesamtwerte seit Datenerfassung</div>
        <p className="text-xs text-slate-400 mt-1">
          Plattformübergreifende Summe aller synchronisierten Beiträge – unabhängig vom Zeitraum des Wochenreports.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {kpis.map(([label, val, Icon]) => (
          <Card key={label} className="p-4 rounded-md border-slate-200 shadow-none">
            <div className="flex items-center justify-between"><span className="overline">{label}</span><Icon size={14} className="text-slate-400" /></div>
            <div className="font-display text-2xl font-extrabold mt-1">{fmt(val)}</div>
          </Card>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5 rounded-md border-slate-200 shadow-none">
          <div className="overline mb-4">Reichweite & Engagement (Zeitverlauf)</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.timeseries}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0A0A0A" stopOpacity={0.15} /><stop offset="100%" stopColor="#0A0A0A" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Area type="monotone" dataKey="views" stroke="#0A0A0A" fill="url(#g)" strokeWidth={2} name="Views" />
              <Area type="monotone" dataKey="engagement" stroke="#0ea5e9" fill="transparent" strokeWidth={2} name="Engagement" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5 rounded-md border-slate-200 shadow-none">
          <div className="overline mb-4">Views nach Plattform</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={platformViews}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="platform" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="chart_views" fill="#0A0A0A" radius={[4, 4, 0, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
          {unavailableViews.length > 0 && (
            <div className="text-[11px] text-slate-500 mt-2">
              {unavailableViews.map(row => row.platform === 'tiktok'
                ? "TikTok: Kennzahlen nicht abrufbar – Leserechte, öffentliche Post-ID und API-Unterstützung prüfen. Fehlende Werte sind keine Null-Aufrufe."
                : `${row.platform}: Für diese Beiträge ist keine View-Zahl verfügbar.`).join(' ')}
            </div>
          )}
          {partialViews.length > 0 && (
            <div className="text-[11px] text-slate-500 mt-2">
              {partialViews.map((row) => row.platform).join(", ")}: Angezeigt werden die verfügbaren Views; einzelne ältere Beiträge enthalten keine View-Zahl.
            </div>
          )}
        </Card>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <TopFlop title="Top-Posts" icon={TrendingUp} rows={data.top} tone="emerald" />
        <TopFlop title="Flop-Posts" icon={TrendingDown} rows={data.flop} tone="rose" />
      </div>
    </div>
  );
};

export const TopFlop = ({ title, icon: Icon, rows = [], tone }) => (
  <Card className="p-5 rounded-md border-slate-200 shadow-none">
    <div className="flex items-center gap-2 mb-4"><Icon size={16} className={tone === "emerald" ? "text-emerald-600" : "text-rose-600"} /><div className="overline !text-slate-600">{title}</div><span className="ml-auto text-xs text-slate-400">{rows.length} Beiträge</span></div>
    <div role="region" aria-label={title} tabIndex={0} data-testid={`analytics-${tone === "emerald" ? "top" : "flop"}-scroll`}
      className="max-h-80 overflow-y-auto overscroll-contain pr-2 divide-y divide-slate-100 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
      {rows.map((r, i) => (
        <div key={r.post_id || i} data-testid="analytics-ranking-row" className="flex items-center gap-3 py-2">
          <span className="font-mono text-xs text-slate-400 w-10 shrink-0">#{i + 1}</span>
          <PlatformBadge platform={r.platform} />
          <div className="flex-1 min-w-0"><div className="text-sm truncate text-slate-700">{r.caption}</div><div className="text-[11px] text-slate-400">Serie {r.series} · ER {r.engagement_rate}%</div></div>
          <div className="text-right"><div className="font-mono font-semibold text-sm">{r.views_available === false ? '—' : fmt(r.views)}</div><div className="text-[10px] text-slate-400">{r.views_available === false ? 'Keine View-Zahl' : `Score ${r.score}`}</div></div>
        </div>
      ))}
    </div>
  </Card>
);
